/* ==========================================================================
   Wheel Offset and Backspacing Calculator
   Self-contained. Loaded only on this calculator's page.
   No shared dependencies.
   ========================================================================== */
(function () {
  'use strict';

  var root = document.querySelector('[data-pc-woc]');
  if (!root) return;

  var MM_PER_INCH = 25.4;
  /* A wheel's overall width is its stated width plus about 1 inch, because the
     stated width is measured bead seat to bead seat and each flange adds
     roughly half an inch. */
  var FLANGE_ALLOWANCE_IN = 1.0;

  /* ====================================================================
     compute(input) -> result

     PURE. No DOM, no network, no globals. All calculator logic lives here
     so it can be reasoned about and tested in isolation.
     ==================================================================== */
  function geom(widthIn, offsetMm) {
    var overallIn = widthIn + FLANGE_ALLOWANCE_IN;
    var halfMm = (overallIn * MM_PER_INCH) / 2;
    return {
      widthIn: widthIn,
      offsetMm: offsetMm,
      /* Distance from the hub mounting face to each edge. */
      outboardMm: halfMm - offsetMm,
      inboardMm: halfMm + offsetMm,
      backspacingIn: ((overallIn * MM_PER_INCH) / 2 + offsetMm) / MM_PER_INCH
    };
  }

  function compute(input) {
    if (!isFinite(input.w1) || input.w1 <= 0 || !isFinite(input.w2) || input.w2 <= 0) {
      return { ok: false, errors: ['Enter both wheel widths.'] };
    }
    if (!isFinite(input.o1) || !isFinite(input.o2)) {
      return { ok: false, errors: ['Enter both offsets. Use a negative number for negative offset.'] };
    }

    var a = geom(input.w1, input.o1);
    var b = geom(input.w2, input.o2);

    /* Positive poke means the new wheel sits further outboard (toward the fender).
       Positive tuck means the new wheel sits further inboard (toward the strut). */
    var poke = b.outboardMm - a.outboardMm;
    var inboard = b.inboardMm - a.inboardMm;

    var status;
    var worst = Math.max(Math.abs(poke), Math.abs(inboard));
    if (worst < 5) {
      status = { key: 'good', label: 'Essentially the same',
                 note: 'Within 5mm of your current setup. No clearance surprises.' };
    } else if (worst <= 12) {
      status = { key: 'good', label: 'Minor change',
                 note: 'A small shift. Usually fine on a stock vehicle, but check at full lock.' };
    } else if (worst <= 25) {
      status = { key: 'soon', label: 'Noticeable change',
                 note: 'Big enough to matter. Measure your actual clearance before buying.' };
    } else {
      status = { key: 'now', label: 'Large change',
                 note: 'This will change where the wheel sits significantly. Expect fender or suspension clearance work.' };
    }

    var warnings = [];
    if (inboard > 12) {
      warnings.push('The new wheel sits ' + num(inboard, 0) + 'mm further inboard. That is the direction of your struts, control arms and brake calipers, which is where clearance runs out first.');
    } else if (poke > 20) {
      warnings.push('The new wheel pokes ' + num(poke, 0) + 'mm further out. Check fender clearance at full compression, and check your local rules on tire coverage.');
    }

    return {
      ok: true, errors: [], warnings: warnings, status: status,
      figures: [
        { label: 'Outboard (poke)', value: signed(poke, 1) + ' mm',
          sub: poke > 0 ? 'Sticks out further than your current wheel.'
             : (poke < 0 ? 'Tucks in further than your current wheel.' : 'No change at the fender.') },
        { label: 'Inboard (toward suspension)', value: signed(inboard, 1) + ' mm',
          sub: inboard > 0 ? 'Moves closer to struts, arms and calipers.'
             : (inboard < 0 ? 'Moves away from the suspension.' : 'No change inboard.') }
      ],
      rows: [
        { head: 'Current wheel' },
        { label: 'Width', value: num(a.widthIn, 1) + ' in' },
        { label: 'Offset', value: signed(a.offsetMm, 0) + ' mm' },
        { label: 'Backspacing', value: num(a.backspacingIn, 2) + ' in' },
        { head: 'New wheel' },
        { label: 'Width', value: num(b.widthIn, 1) + ' in' },
        { label: 'Offset', value: signed(b.offsetMm, 0) + ' mm' },
        { label: 'Backspacing', value: num(b.backspacingIn, 2) + ' in' },
        { head: 'Change' },
        { label: 'Width', value: signed(b.widthIn - a.widthIn, 1) + ' in' },
        { label: 'Offset', value: signed(b.offsetMm - a.offsetMm, 0) + ' mm' },
        { label: 'Backspacing', value: signed(b.backspacingIn - a.backspacingIn, 2) + ' in' },
        { label: 'Track width per side', value: signed(poke, 1) + ' mm' }
      ]
    };
  }

  /* ====================================================================
     Formatting
     ==================================================================== */
  function comma(n) {
    return String(n).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  function num(n, dp) {
    if (!isFinite(n)) return '-';
    var v = Number(n).toFixed(dp == null ? 2 : dp);
    if (dp !== 0 && v.indexOf('.') > -1) v = v.replace(/\.?0+$/, '');
    var parts = v.split('.');
    return comma(parts[0]) + (parts[1] ? '.' + parts[1] : '');
  }

  function signed(n, dp) {
    if (!isFinite(n)) return '-';
    return (n > 0 ? '+' : '') + num(n, dp);
  }

  /* ====================================================================
     DOM wiring
     ==================================================================== */
  var FIELDS = [
      {
          "name": "w1",
          "key": "w1",
          "type": "number"
      },
      {
          "name": "o1",
          "key": "o1",
          "type": "number"
      },
      {
          "name": "w2",
          "key": "w2",
          "type": "number"
      },
      {
          "name": "o2",
          "key": "o2",
          "type": "number"
      }
  ];

  var els = {
    error:    root.querySelector('[data-r-error]'),
    status:   root.querySelector('[data-r-status]'),
    statusLbl:root.querySelector('[data-r-status-label]'),
    statusVal:root.querySelector('[data-r-status-value]'),
    statusNote:root.querySelector('[data-r-status-note]'),
    figures:  root.querySelector('[data-r-figures]'),
    rows:     root.querySelector('[data-r-rows]'),
    warnings: root.querySelector('[data-r-warnings]')
  };

  var STATUS_CLASSES = ['is-good','is-monitor','is-soon','is-now','is-critical'];

  function field(name) { return root.querySelector('[data-f="' + name + '"]'); }

  function readInput() {
    var o = {};
    FIELDS.forEach(function (f) {
      var el = field(f.name);
      if (!el) return;
      if (f.type === 'checkbox') o[f.name] = el.checked;
      else if (f.type === 'number') o[f.name] = parseFloat(el.value);
      else o[f.name] = el.value;
    });
    return o;
  }

  function esc(s) {
    return String(s == null ? '' : s)
      .replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;');
  }

  function renderFigures(figs) {
    els.figures.innerHTML = (figs || []).map(function (f) {
      return '<div class="pc-woc__figure">'
        + '<div class="pc-woc__figure-label">' + esc(f.label) + '</div>'
        + '<div class="pc-woc__figure-value' + (f.muted ? ' is-muted' : '') + '">'
        + esc(f.value) + '</div>'
        + (f.sub ? '<div class="pc-woc__figure-sub">' + esc(f.sub) + '</div>' : '')
        + '</div>';
    }).join('');
  }

  function renderRows(rows) {
    if (!els.rows) return;
    els.rows.innerHTML = (rows || []).map(function (r) {
      if (r.head) {
        return '<div class="pc-woc__row pc-woc__rows-head">' + esc(r.head) + '</div>';
      }
      return '<div class="pc-woc__row">'
        + '<span class="pc-woc__row-label">' + esc(r.label) + '</span>'
        + '<span class="pc-woc__row-value">' + esc(r.value) + '</span>'
        + '</div>';
    }).join('');
  }

  function clearResult(message) {
    if (els.status) {
      STATUS_CLASSES.forEach(function (c) { els.status.classList.remove(c); });
      els.statusVal.textContent  = 'Waiting for your numbers';
      els.statusNote.textContent = message || '';
    }
    els.figures.innerHTML = '';
    if (els.rows) els.rows.innerHTML = '';
    if (els.warnings) els.warnings.style.display = 'none';
  }

  function render() {
    var input  = readInput();
    var result = compute(input);

    if (!result.ok) {
      els.error.textContent = result.errors[0];
      els.error.classList.add('is-shown');
      clearResult(result.errors[0]);
      return;
    }
    els.error.classList.remove('is-shown');

    if (els.status) {
      STATUS_CLASSES.forEach(function (c) { els.status.classList.remove(c); });
      if (result.status) {
        els.status.classList.add('is-' + result.status.key);
        els.statusVal.textContent  = result.status.label;
        els.statusNote.textContent = result.status.note || '';
      } else {
        els.statusVal.textContent  = '';
        els.statusNote.textContent = '';
      }
    }

    renderFigures(result.figures);
    renderRows(result.rows);

    if (els.warnings) {
      if (result.warnings && result.warnings.length) {
        els.warnings.textContent = result.warnings[0];
        els.warnings.style.display = '';
      } else {
        els.warnings.style.display = 'none';
      }
    }

    writePermalink(input);
  }

  /* ====================================================================
     Shareable result permalinks
     ==================================================================== */
  function writePermalink(input) {
    if (!window.history || !window.history.replaceState) return;
    var q = FIELDS.map(function (f) {
      var v = input[f.name];
      if (f.type === 'checkbox') return v ? f.key + '=1' : '';
      if (v == null || v !== v || v === '') return '';
      return f.key + '=' + encodeURIComponent(v);
    }).filter(Boolean).join('&');
    try {
      window.history.replaceState(null, '', window.location.pathname + (q ? '?' + q : ''));
    } catch (e) {}
  }

  function applyPermalink() {
    if (!window.URLSearchParams) return;
    var p = new URLSearchParams(window.location.search);
    var any = false;
    FIELDS.forEach(function (f) {
      if (!p.has(f.key)) return;
      var el = field(f.name);
      if (!el) return;
      if (f.type === 'checkbox') el.checked = p.get(f.key) === '1';
      else el.value = p.get(f.key);
      any = true;
    });
    return any;
  }

  /* ====================================================================
     Init
     ==================================================================== */
  ['input', 'change'].forEach(function (evt) {
    root.addEventListener(evt, function (e) {
      if (e.target.closest('[data-r-form]')) render();
    });
  });

  applyPermalink();
  render();

  /* Exposed for testing and debugging only. */
  window.PC_WOC = { compute: compute };
})();
